import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { calendarKeys } from 'core/utils/query-key-factory';
import { CalendarViewTypes } from '../types';
import { getCalendarViewPath } from './calendar-view-path';

type CalendarState = {
	view?: CalendarViewTypes;
};

export const RestoreView = ({ calendarId }: { calendarId: string }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const hasRestored = useRef(false);

	useEffect(() => {
		// Only restore on initial mount, not on every location change
		if (hasRestored.current) return;

		const stateKey = calendarKeys.state(calendarId);
		const cached = queryClient.getQueryData<CalendarState>(stateKey);
		const savedView = cached?.view;

		if (!savedView) {
			hasRestored.current = true;
			return;
		}

		// Check if current route matches the saved view
		// The pathname ends with the view name (e.g., /community/123/calendar/456/year)
		// or the calendarId if on index route
		const pathSegments = location.pathname.split('/').filter(Boolean);
		const lastSegment = pathSegments[pathSegments.length - 1];
		const currentView =
			lastSegment === calendarId ? undefined : lastSegment;

		// If we're on the index route (last segment is calendarId) or the view doesn't match, navigate to saved view
		if (currentView !== savedView) {
			navigate(getCalendarViewPath(location.pathname, savedView), {
				replace: true,
			});
		}

		hasRestored.current = true;
	}, [calendarId, location.pathname, navigate, queryClient]);

	return null;
};
